/**
 * Typed API client — thin wrapper around fetch() for /api/* endpoints.
 *
 * Conventions:
 *   - Same-origin cookies (credentials: 'same-origin') — session cookie set by
 *     /api/auth/login flows automatically.
 *   - 401 → throws ApiAuthError; UI redirects to /login.html
 *   - 4xx → throws ApiClientError with .status + .body for granular handling
 *   - 5xx → throws ApiServerError
 *   - Successful (2xx) JSON → returns parsed body typed via generic T
 *
 * Used by TanStack Query queryFn implementations and direct fetch sites.
 *
 * Phase 11 Block 6 PR 1.
 */

export class ApiAuthError extends Error {
  status = 401;
  constructor(message = 'Not authenticated') { super(message); this.name = 'ApiAuthError'; }
}

export class ApiClientError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export class ApiServerError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
    this.name = 'ApiServerError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
}

// Correlation id sent on every API call as `x-request-id`, so a frontend action and its
// backend log line / Sentry event share one id. crypto.randomUUID is available in all
// browsers this app targets.
function newRequestId(): string {
  try { return crypto.randomUUID(); } catch { return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
}

async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = opts;
  const headers: Record<string, string> = { 'x-request-id': newRequestId() };
  if (body) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  // Try to parse body — but some endpoints return non-JSON (e.g. CSV).
  // For CSV/non-JSON paths, caller should use fetch directly.
  const text = await res.text();
  let parsed: unknown = text;
  try { parsed = text ? JSON.parse(text) : null; } catch { /* keep as text */ }

  if (res.ok) return parsed as T;

  if (res.status === 401) throw new ApiAuthError();
  if (res.status >= 400 && res.status < 500) {
    throw new ApiClientError(res.status, parsed, `HTTP ${res.status}: ${typeof parsed === 'object' && parsed && 'error' in parsed ? (parsed as { error: string }).error : 'Client error'}`);
  }
  throw new ApiServerError(res.status, parsed, `HTTP ${res.status}: server error`);
}

export const api = {
  get: <T = unknown>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T = unknown>(path: string, body?: unknown, signal?: AbortSignal) => request<T>(path, { method: 'POST', body, signal }),
  put: <T = unknown>(path: string, body?: unknown, signal?: AbortSignal) => request<T>(path, { method: 'PUT', body, signal }),
  delete: <T = unknown>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'DELETE', signal }),
  patch: <T = unknown>(path: string, body?: unknown, signal?: AbortSignal) => request<T>(path, { method: 'PATCH', body, signal }),
};

/**
 * Direct fetch URL builder for non-JSON endpoints (CSV exports, file downloads).
 * Returns the URL string; caller does `window.location = downloadUrl(...)` or similar.
 */
export function downloadUrl(path: string): string {
  return path;  // currently just a passthrough; centralized in case base path / query encoding evolves
}
